import { get, post, put, del } from '@aiptyg/api-client';
import type {
  InvitationPolicyDTO,
  InvitationRulesDTO,
  CommissionPolicyDTO,
  NewcomerRewardPolicyDTO,
  NewcomerRewardPolicyUpdateParams,
  WithdrawalPolicyDTO,
  TaskRiskPolicyDTO,
  WithdrawalRiskPolicyDTO,
  WithdrawalRiskPolicyUpdateParams,
  WithdrawalPayoutConfigDTO,
  WithdrawalPayoutConfigUpdateParams,
  MediaStorageConfigDTO,
  MediaStorageConfigUpdateParams,
  WithdrawalRedPacketConfigDTO,
  WithdrawalRedPacketConfigUpdateParams,
  CaptchaConfigDTO,
  CaptchaConfigUpdateParams,
  OCRConfigDTO,
  OCRConfigUpdateParams,
  VerificationEmailConfigDTO,
  VerificationEmailConfigUpdateParams,
  HomeBannerConfigDTO,
  HomeBannerUpsertParams,
  HomeAnnouncementConfigDTO,
  HomeAnnouncementUpsertParams,
  HomePopupConfigDTO,
  HomePopupUpdateParams,
  CustomerServiceConfigDTO,
  CustomerServiceConfigUpdateParams,
  FAQConfigDTO,
  FAQConfigUpdateParams,
  AppUpdateConfigDTO,
  AppUpdateConfigUpdateParams,
  AppleAuthConfigDTO,
  AppleAuthConfigUpdateParams,
  MessageReviewConfigDTO,
  MessageReviewConfigUpdateParams,
  MessageReviewTestResultDTO,
  MarketConfigListDTO,
  MarketConfigUpdateParams,
} from '../types/settings.types';

export function getInvitationPolicy(): Promise<InvitationPolicyDTO> {
  return get<InvitationPolicyDTO>('/v1/admin/settings/invitation-policy');
}

export async function updateInvitationPolicy(requireInviteCode: boolean): Promise<void> {
  await put('/v1/admin/settings/invitation-policy', { require_invite_code: requireInviteCode });
}

export function getInvitationRules(): Promise<InvitationRulesDTO> {
  return get<InvitationRulesDTO>('/v1/admin/settings/invitation-rules');
}

export async function updateInvitationRules(params: {
  market_id: string;
  steps: Array<{ title: string; description: string }>;
  rules: string[];
}): Promise<void> {
  await put('/v1/admin/settings/invitation-rules', {
    market_id: params.market_id,
    steps: params.steps,
    rules: params.rules,
  });
}

export function getCommissionPolicy(marketId = 'CN'): Promise<CommissionPolicyDTO> {
  return get<CommissionPolicyDTO>(`/v1/admin/settings/commission-policy?market_id=${encodeURIComponent(marketId)}`);
}

export async function updateCommissionPolicy(params: {
  market_id: string;
  level1FixedFen: number;
  level1Percentage: number;
  level2FixedFen: number;
  level2Percentage: number;
  level3FixedFen: number;
  level3Percentage: number;
}): Promise<void> {
  await put('/v1/admin/settings/commission-policy', {
    market_id: params.market_id,
    level1_fixed_fen: params.level1FixedFen,
    level1_percentage: params.level1Percentage,
    level2_fixed_fen: params.level2FixedFen,
    level2_percentage: params.level2Percentage,
    level3_fixed_fen: params.level3FixedFen,
    level3_percentage: params.level3Percentage,
  });
}

export function getNewcomerRewardPolicy(marketId = 'CN'): Promise<NewcomerRewardPolicyDTO> {
  return get<NewcomerRewardPolicyDTO>(`/v1/admin/settings/newcomer-reward?market_id=${encodeURIComponent(marketId)}`);
}

export function updateNewcomerRewardPolicy(params: NewcomerRewardPolicyUpdateParams): Promise<NewcomerRewardPolicyDTO> {
  return put<NewcomerRewardPolicyDTO>('/v1/admin/settings/newcomer-reward', {
    market_id: params.market_id,
    enabled: params.enabled,
    amount_fen: params.amountFen,
  });
}

export function getWithdrawalPolicy(): Promise<WithdrawalPolicyDTO> {
  return get<WithdrawalPolicyDTO>('/v1/admin/settings/withdrawal-policy');
}

export async function updateWithdrawalPolicy(params: {
  minimumAmountFen: number;
  firstMinimumAmountFen: number;
  dailyLimit: number;
  bankCardEnabled: boolean;
  alipayEnabled: boolean;
  windowEnabled: boolean;
  windowStartMinute: number;
  windowEndMinute: number;
  windowTimezone: string;
  newUserRequiredCompletedTasks: number;
}): Promise<void> {
  await put('/v1/admin/settings/withdrawal-policy', {
    minimum_amount_fen: params.minimumAmountFen,
    first_minimum_amount_fen: params.firstMinimumAmountFen,
    daily_limit: params.dailyLimit,
    bank_card_enabled: params.bankCardEnabled,
    alipay_enabled: params.alipayEnabled,
    window_enabled: params.windowEnabled,
    window_start_minute: params.windowStartMinute,
    window_end_minute: params.windowEndMinute,
    window_timezone: params.windowTimezone,
    new_user_required_completed_tasks: params.newUserRequiredCompletedTasks,
  });
}

export function getTaskRiskPolicy(): Promise<TaskRiskPolicyDTO> {
  return get<TaskRiskPolicyDTO>('/v1/admin/settings/task-risk-policy');
}

export function updateTaskRiskPolicy(params: {
  maxEffectiveAssignmentsPerTaskIP: number;
  ipMatchMode: 'exact' | 'ipv4_24' | 'ipv4_16';
  claimRequireWithdrawalAccount: boolean;
}): Promise<TaskRiskPolicyDTO> {
  return put<TaskRiskPolicyDTO>('/v1/admin/settings/task-risk-policy', {
    max_effective_assignments_per_task_ip: params.maxEffectiveAssignmentsPerTaskIP,
    ip_match_mode: params.ipMatchMode,
    claim_require_withdrawal_account: params.claimRequireWithdrawalAccount,
  });
}

export function getWithdrawalRiskPolicy(): Promise<WithdrawalRiskPolicyDTO> {
  return get<WithdrawalRiskPolicyDTO>('/v1/admin/settings/withdrawal-risk-policy');
}

export function updateWithdrawalRiskPolicy(
  params: WithdrawalRiskPolicyUpdateParams,
): Promise<WithdrawalRiskPolicyDTO> {
  return put<WithdrawalRiskPolicyDTO>('/v1/admin/settings/withdrawal-risk-policy', {
    same_target_user_threshold: params.same_target_user_threshold,
    telegram_enabled: params.telegram_enabled,
    telegram_bot_token: params.telegram_bot_token,
    telegram_chat_id: params.telegram_chat_id,
  });
}

export function getWithdrawalPayoutConfig(): Promise<WithdrawalPayoutConfigDTO> {
  return get<WithdrawalPayoutConfigDTO>('/v1/admin/settings/withdrawal-payout');
}

export function updateWithdrawalPayoutConfig(
  params: WithdrawalPayoutConfigUpdateParams,
): Promise<WithdrawalPayoutConfigDTO> {
  return put<WithdrawalPayoutConfigDTO>('/v1/admin/settings/withdrawal-payout', {
    enabled: true,
    auto_review_enabled: params.auto_review_enabled,
    provider: params.provider,
    sypaymon: params.sypaymon,
    alipay_direct_config: params.alipay_direct_config,
    alipay_direct_accounts: params.alipay_direct_accounts,
    csjvnpay: params.csjvnpay,
    app_id: params.app_id,
    secret: params.secret,
    base_url: params.base_url,
    alipay_public_key: params.alipay_public_key,
    alipay_app_cert: params.alipay_app_cert,
    alipay_public_cert: params.alipay_public_cert,
    alipay_root_cert: params.alipay_root_cert,
    default_mode: params.default_mode,
    auto_channels: params.auto_channels,
    max_auto_amount_fen: params.max_auto_amount_fen,
    retry_limit: params.retry_limit,
    query_interval_seconds: params.query_interval_seconds,
    bank_card_enabled: params.bank_card_enabled,
    alipay_enabled: params.alipay_enabled,
    alipay_product_id: params.alipay_product_id,
    alipay_amounts: params.alipay_amounts,
    fixed_amounts: params.fixed_amounts,
    bank_card_product_id: params.bank_card_product_id,
    notify_path: params.notify_path,
    allow_user_ids: params.allow_user_ids,
    deny_user_ids: params.deny_user_ids,
  });
}

export function getMediaStorageConfig(): Promise<MediaStorageConfigDTO> {
  return get<MediaStorageConfigDTO>('/v1/admin/settings/media-storage');
}

export function updateMediaStorageConfig(
  params: MediaStorageConfigUpdateParams,
): Promise<MediaStorageConfigDTO> {
  return put<MediaStorageConfigDTO>('/v1/admin/settings/media-storage', {
    bucket: params.bucket,
    region: params.region,
    public_domain: params.public_domain,
    secret_id: params.secret_id,
    secret_key: params.secret_key,
  });
}

export function getWithdrawalRedPacketConfig(): Promise<WithdrawalRedPacketConfigDTO> {
  return get<WithdrawalRedPacketConfigDTO>('/v1/admin/settings/withdrawal-red-packet');
}

export function updateWithdrawalRedPacketConfig(
  params: WithdrawalRedPacketConfigUpdateParams,
): Promise<WithdrawalRedPacketConfigDTO> {
  const yaoyaola: Record<string, unknown> = {
    enabled: params.yaoyaola.enabled,
    uid: params.yaoyaola.uid,
    backend_base_url: params.yaoyaola.backend_base_url,
    auth_domain_cache_seconds: params.yaoyaola.auth_domain_cache_seconds,
    auth_callback_url: params.yaoyaola.auth_callback_url,
    type: params.yaoyaola.type,
    title: params.yaoyaola.title,
    sendname: params.yaoyaola.sendname,
    wishing: params.yaoyaola.wishing,
    min_amount_fen: params.yaoyaola.min_amount_fen,
    max_amount_fen: params.yaoyaola.max_amount_fen,
    amounts: params.yaoyaola.amounts,
  };
  if (params.yaoyaola.api_key?.trim()) {
    yaoyaola.api_key = params.yaoyaola.api_key.trim();
  }

  return put<WithdrawalRedPacketConfigDTO>('/v1/admin/settings/withdrawal-red-packet', {
    enabled: params.enabled,
    provider_priority: params.provider_priority,
    callback_domain: params.callback_domain,
    jinshuju: params.jinshuju,
    yaoyaola,
  });
}

export function getCaptchaConfig(): Promise<CaptchaConfigDTO> {
  return get<CaptchaConfigDTO>('/v1/admin/settings/captcha');
}

export function updateCaptchaConfig(params: CaptchaConfigUpdateParams): Promise<CaptchaConfigDTO> {
  return put<CaptchaConfigDTO>('/v1/admin/settings/captcha', {
    provider: params.provider,
    task_claim_provider: params.task_claim_provider,
    aliyun: params.aliyun,
    tencent: params.tencent,
  });
}

export function deleteCaptchaProvider(provider: 'aliyun' | 'tencent'): Promise<CaptchaConfigDTO> {
  return del<CaptchaConfigDTO>(`/v1/admin/settings/captcha/providers/${provider}`);
}

export function getOCRConfig(): Promise<OCRConfigDTO> {
  return get<OCRConfigDTO>('/v1/admin/settings/ocr');
}

export function updateOCRConfig(params: OCRConfigUpdateParams): Promise<OCRConfigDTO> {
  return put<OCRConfigDTO>('/v1/admin/settings/ocr', {
    normal_task_ocr_claim_workers: params.normal_task_ocr_claim_workers,
    task_priority: params.task_priority,
    enabled: params.enabled,
    provider: params.provider,
    tencent: params.tencent,
    paddleocr: params.paddleocr,
  });
}

export function getVerificationEmailConfig(): Promise<VerificationEmailConfigDTO> {
  return get<VerificationEmailConfigDTO>('/v1/admin/settings/verification-email');
}

export function updateVerificationEmailConfig(params: VerificationEmailConfigUpdateParams): Promise<VerificationEmailConfigDTO> {
  return put<VerificationEmailConfigDTO>('/v1/admin/settings/verification-email', {
    provider: params.provider,
    aliyun: params.aliyun,
    tencent: params.tencent,
  });
}

export function deleteVerificationEmailProvider(provider: 'aliyun' | 'tencent'): Promise<VerificationEmailConfigDTO> {
  return del<VerificationEmailConfigDTO>(`/v1/admin/settings/verification-email/providers/${provider}`);
}

// --- Home Banners ---

export function getHomeBanners(): Promise<HomeBannerConfigDTO> {
  return get<HomeBannerConfigDTO>('/v1/admin/settings/home-banners');
}

export function createHomeBanner(params: HomeBannerUpsertParams): Promise<HomeBannerConfigDTO> {
  return post<HomeBannerConfigDTO>('/v1/admin/settings/home-banners', params);
}

export function updateHomeBanner(id: string, params: HomeBannerUpsertParams): Promise<HomeBannerConfigDTO> {
  return put<HomeBannerConfigDTO>(`/v1/admin/settings/home-banners/${id}`, params);
}

export function deleteHomeBanner(id: string): Promise<HomeBannerConfigDTO> {
  return del<HomeBannerConfigDTO>(`/v1/admin/settings/home-banners/${id}`);
}

// --- Home Announcements ---

export function getHomeAnnouncements(): Promise<HomeAnnouncementConfigDTO> {
  return get<HomeAnnouncementConfigDTO>('/v1/admin/settings/home-announcements');
}

export function createHomeAnnouncement(params: HomeAnnouncementUpsertParams): Promise<HomeAnnouncementConfigDTO> {
  return post<HomeAnnouncementConfigDTO>('/v1/admin/settings/home-announcements', params);
}

export function updateHomeAnnouncement(id: string, params: HomeAnnouncementUpsertParams): Promise<HomeAnnouncementConfigDTO> {
  return put<HomeAnnouncementConfigDTO>(`/v1/admin/settings/home-announcements/${id}`, params);
}

export function deleteHomeAnnouncement(id: string): Promise<HomeAnnouncementConfigDTO> {
  return del<HomeAnnouncementConfigDTO>(`/v1/admin/settings/home-announcements/${id}`);
}

// --- Home Popup ---

export function getHomePopup(): Promise<HomePopupConfigDTO> {
  return get<HomePopupConfigDTO>('/v1/admin/settings/home-popup');
}

export function updateHomePopup(params: HomePopupUpdateParams): Promise<HomePopupConfigDTO> {
  return put<HomePopupConfigDTO>('/v1/admin/settings/home-popup', params);
}

// --- Customer Service ---

export function getCustomerServiceConfig(): Promise<CustomerServiceConfigDTO> {
  return get<CustomerServiceConfigDTO>('/v1/admin/settings/customer-service');
}

export function updateCustomerServiceConfig(
  params: CustomerServiceConfigUpdateParams,
): Promise<CustomerServiceConfigDTO> {
  return put<CustomerServiceConfigDTO>('/v1/admin/settings/customer-service', {
    market_id: params.market_id,
    enabled: params.enabled,
    script: params.script,
    channel_id: params.channel_id,
    channels: params.channels,
  });
}

// --- FAQ ---

export function getFAQConfig(): Promise<FAQConfigDTO> {
  return get<FAQConfigDTO>('/v1/admin/settings/faq');
}

export function updateFAQConfig(params: FAQConfigUpdateParams): Promise<FAQConfigDTO> {
  return put<FAQConfigDTO>('/v1/admin/settings/faq', {
    items: params.items,
  });
}

// --- App Update ---

export function getAppUpdateConfig(): Promise<AppUpdateConfigDTO> {
  return get<AppUpdateConfigDTO>('/v1/admin/settings/app-update');
}

export function updateAppUpdateConfig(
  params: AppUpdateConfigUpdateParams,
): Promise<AppUpdateConfigDTO> {
  return put<AppUpdateConfigDTO>('/v1/admin/settings/app-update', {
    market_id: params.market_id,
    ios: params.ios,
    android: params.android,
  });
}

// --- Apple Auth ---

export function getAppleAuthConfig(): Promise<AppleAuthConfigDTO> {
  return get<AppleAuthConfigDTO>('/v1/admin/settings/apple-auth');
}

export function updateAppleAuthConfig(
  params: AppleAuthConfigUpdateParams,
): Promise<AppleAuthConfigDTO> {
  return put<AppleAuthConfigDTO>('/v1/admin/settings/apple-auth', {
    auth_endpoint: params.auth_endpoint,
    email_domain_restriction_enabled: params.email_domain_restriction_enabled,
    allowed_email_domains: params.allowed_email_domains,
  });
}

// --- Message Review ---

export function getMessageReviewConfig(): Promise<MessageReviewConfigDTO> {
  return get<MessageReviewConfigDTO>('/v1/admin/settings/message-review');
}

export function updateMessageReviewConfig(
  params: MessageReviewConfigUpdateParams,
): Promise<MessageReviewConfigDTO> {
  return put<MessageReviewConfigDTO>('/v1/admin/settings/message-review', {
    enabled: params.enabled,
    provider: params.provider,
    model: params.model,
    reasoning_effort: params.reasoning_effort,
    manual_review_ratio: params.manual_review_ratio,
    api_key: params.api_key.trim(),
    local_ocr_base_url: params.local_ocr_base_url.trim(),
    local_ocr_max_concurrency: params.local_ocr_max_concurrency,
    local_ocr_cpu_threads: params.local_ocr_cpu_threads,
    timeout_seconds: params.timeout_seconds,
    max_retries: params.max_retries,
    confidence_threshold: params.confidence_threshold,
  });
}

export function testMessageReviewConfig(): Promise<MessageReviewTestResultDTO> {
  return post<MessageReviewTestResultDTO>('/v1/admin/settings/message-review/test', {});
}

// --- Markets ---

export function getMarketConfigs(): Promise<MarketConfigListDTO> {
  return get<MarketConfigListDTO>('/v1/admin/settings/markets');
}

export function updateMarketConfig(
  marketId: string,
  params: MarketConfigUpdateParams,
): Promise<MarketConfigListDTO> {
  return put<MarketConfigListDTO>(`/v1/admin/settings/markets/${marketId}`, {
    name: params.name,
    locale: params.locale,
    currency: params.currency,
    currency_symbol: params.currency_symbol,
    currency_exponent: params.currency_exponent,
    enabled: true,
    enabled_task_types: params.enabled_task_types,
    enabled_features: params.enabled_features,
    country_confirmation_required: params.country_confirmation_required,
    task_configs: params.task_configs,
    withdrawal_config: params.withdrawal_config,
  });
}
